import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useState } from "react";
import { PATH_INFO } from "../util/helper";
import bgImg from "../../assets/images/home-bg.svg";
import AiMeasureLogo from "../../assets/images/logos/ai-measure-logo-black.svg";
import toyoshimaLogo from "../../assets/images/logos/toyoshima-black.svg";
import { Button, Input } from "antd";
import { UpdateAuthContext } from "../../context/authContext";
import { isValidEmail } from "../../utils/validation";

const VALIDATION_MESSAGES = {
  emailRequired: 'メールアドレスを入力してください。',
  emailInvalid: 'メールアドレスの形式が正しくありません。',
  passwordRequired: 'パスワードを入力してください。',
  loginFailed: 'ログインに失敗しました。',
}

export function Login() {
  const { login } = useContext(UpdateAuthContext)
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState({ email: '', password: '' })
  const [errorMessage, setErrorMessage] = useState('')

  const onSubmitLogin = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedEmail = userInfo.email.trim()
    const loginPayload = { ...userInfo, email: trimmedEmail }
    if (!trimmedEmail) {
      setErrorMessage(VALIDATION_MESSAGES.emailRequired)
      return
    }
    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage(VALIDATION_MESSAGES.emailInvalid)
      return
    }
    if (!userInfo.password.trim()) {
      setErrorMessage(VALIDATION_MESSAGES.passwordRequired)
      return
    }

    try {
      const result = await login(loginPayload)
      if (result.ok) {
        navigate(PATH_INFO.COMPANY_LIST.PATH)
      } else {
        setErrorMessage(result.errorMessage || VALIDATION_MESSAGES.loginFailed)
      }
    } catch {
      setErrorMessage(VALIDATION_MESSAGES.loginFailed)
    }
  }, [login, userInfo, navigate]);

  return (
    <div
      className="flex items-center justify-center min-h-[100dvh] px-4 py-6 sm:px-6 bg-cover bg-center box-border"
      style={{ backgroundImage: `url(${bgImg})` }}
    >
      <div className="flex w-full max-w-6xl min-w-[320px] rounded-[10px] overflow-hidden flex-col tablet-lg:flex-row tablet-lg:h-[914px] tablet-lg:min-h-[600px] tablet-lg:max-h-[calc(100vh-3rem)]">
        <div className="flex flex-col justify-between items-center p-6 sm:p-10 tablet-lg:p-12 bg-[#F3F3F3] tablet-lg:w-1/2 gap-6 text-center max-tablet-lg:h-[120px]">
          <p className="text-lg sm:text-xl tablet-lg:text-[24px] m-0 font-[Poppins]">WELCOME AI MEASURE!</p>
          <img src={AiMeasureLogo} alt="logo" className="w-40 sm:w-44 tablet-lg:w-[217px]" />
          <img src={toyoshimaLogo} alt="logo" className="w-28 sm:w-32 tablet-lg:w-[151px] max-tablet-lg:hidden" />
        </div>
        <div className="relative flex flex-col items-center justify-center bg-white tablet-lg:w-1/2 font-[Poppins] px-6 sm:px-10 tablet-lg:px-12 py-10 tablet-lg:py-16 pb-20 tablet-lg:pb-24">
          <p className="font-bold text-lg sm:text-xl tablet-lg:text-[20px] mb-6 tablet-lg:mb-8 m-0">Log in</p>
          <form className="flex flex-col w-full max-w-md" onSubmit={onSubmitLogin}>
            <div className="flex flex-col mb-[20px]">
              <label className="text-[16px]">Email(*)</label>
              <Input
                type="email"
                className="w-full font-normal text-[16px]"
                variant="underlined"
                status={errorMessage ? 'error' : ''}
                aria-describedby="login-error-message"
                aria-invalid={Boolean(errorMessage)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserInfo((prev) => ({ ...prev, email: e.target.value }))
                  setErrorMessage('')
                }}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[16px]">Password</label>
              <Input.Password
                variant='underlined'
                status={errorMessage ? 'error' : ''}
                aria-describedby="login-error-message"
                aria-invalid={Boolean(errorMessage)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUserInfo((prev) => ({ ...prev, password: e.target.value }))
                  setErrorMessage('')
                }}
                className="font-semibold text-[16px]"
              />
              <p
                className={`text-sm text-red-600 mt-2 min-h-[20px] ${errorMessage ? 'visible' : 'invisible'}`}
                role="alert"
                id="login-error-message"
              >
                {errorMessage || ''}
              </p>
            </div>
            <Button
              htmlType="submit"
              color="default"
              variant="solid"
              className="mt-10 tablet-lg:mt-16 w-full max-w-md h-14 sm:h-16 tablet-lg:h-[73px] font-bold text-[16px]"
            >
              login now
            </Button>
          </form>
          <div className="flex justify-center mt-8 tablet-lg:mt-0 tablet-lg:absolute tablet-lg:bottom-[50px] w-full max-w-xs h-[31px] border-b-[1px] border-black">
            <Button color="default" variant="link" className="font-medium underline font-[Poppins]">Forgot your password?</Button>
          </div>
        </div>
        <div className="bg-[#F3F3F3] h-28 flex items-center justify-center tablet-lg:hidden">
          <img src={toyoshimaLogo} alt="logo" className="w-28 sm:w-32 tablet-lg:w-[151px]" />
        </div>
      </div>
    </div>
  );
}
